<template>
	<div class="editor pt-3">
		<div v-if="isMapperMode" class="dynamic-data-mapping-section-container">
			<MappingSetupInfo utilClasses="mx-5 my-1" />
			<ChooseDynamicDataMappingCard
				contextMode
				:node="node"
				:action="contextNodeAction"
				:serviceId="node.service"
				:serviceVersionId="node.service_version"
			/>
		</div>
		<ActionContextNodeParams
			v-if="showActionContextNodeParams"
			:hasEditPermissions="hasEditPermissions"
			:allowChangeSampleFunctionality="allowChangeSampleFunctionality"
			@change-sample-by-choosing-item="changeContextParamsManually = false"
		/>
		<ContextChangeSample
			v-else-if="allowChangeSampleFunctionality"
			:hasEditPermissions="hasEditPermissions"
			:canManuallyChange="canChangeContextNodeSampleManually"
			:sampleItemLabel="sampleItemLabel"
			:workflowId="workflowId"
			@change-manually="changeContextParamsManually = true"
		/>
	</div>
</template>

<script>
import { getAction } from '@/store/modules/actions/getters';
import { PROFILE_FROM_CONFIGURATION_CREDENTIALS_TYPES } from '@/utils/credentials-service.const';
import { mapGettersShorthand } from '@/store/utils/mappers';
import MappingSetupInfo from '../MappingSetup/MappingSetupInfo.vue';
import ChooseDynamicDataMappingCard from '@/components/BlockEditorWidget/WidgetFields/DynamicDataMapping/ChooseDynamicDataMappingCard.vue';
import ActionContextNodeParams from '../ActionContextNodeParams.vue';
import ContextChangeSample from './ContextChangeSample.vue';
import { getNode, getNodeTestResult } from '@/store/modules/nodes/getters';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import { capitalizeWord } from '@/utils/language-service';
import { canEditWorkflow } from '@/store/modules/workflows/getters';
import { selectedWorkflowId } from '@/store/modules/selectedWorkflow/getters';
import { isChangeSampleThroughExtEnabled } from '@/store/modules/featureFlags/getters';
export default {
	name: 'BlockContextualEditorParams',
	props: { workflowId: { type: Number }, node: { type: Object }, placement: { type: Object } },
	components: { MappingSetupInfo, ChooseDynamicDataMappingCard, ActionContextNodeParams, ContextChangeSample },
	computed: {
		...mapGettersShorthand({
			getAction,
			getNodeTestResult,
			getNode,
			canEditWorkflow,
			selectedWorkflowId,
			isChangeSampleThroughExtEnabled,
		}),
		isMapperMode() {
			if (!this.node.credentials_configuration?.source_field_uuid) {
				return false;
			}
			return (
				PROFILE_FROM_CONFIGURATION_CREDENTIALS_TYPES.includes(this.node.credentials_configuration?.credentials_type) &&
				this.contextNodeAction?.has_user_schema
			);
		},
		contextNodeAction() {
			return this.getAction(this.node.action);
		},
		showActionContextNodeParams() {
			return this.canChangeContextNodeSampleManually && this.showManualChangeOfContextParams;
		},
		canChangeContextNodeSampleManually() {
			if (this.contextNodeAction) {
				return !this.contextNodeAction.browserAction; // hide ActionContextNodeParams if the action is browserAction
			}
			return false;
		},
		testResult() {
			return this.getNodeTestResult(this.node.id, false);
		},
		nodeSyncWithServer() {
			return this.getNode(this.node.id);
		},
		sampleItemLabel() {
			const itemName = this.placement.item_name;
			if (WorkflowNodeService.isTestedSuccessfully(this.nodeSyncWithServer)) {
				return WorkflowNodeService.getLabelHiddenFieldFromTestResult(this.testResult, itemName);
			} else if (WorkflowNodeService.isTestedSkipped(this.nodeSyncWithServer)) {
				return `Test has been skipped. ${capitalizeWord(itemName)} is unknown.`;
			}
			return `${itemName} is unknown. Test step to retrieve ${itemName} name.`;
		},
		hasEditPermissions() {
			return this.canEditWorkflow(this.selectedWorkflowId);
		},
		showManualChangeOfContextParams() {
			return this.changeContextParamsManually || !this.allowChangeSampleFunctionality;
		},
		allowChangeSampleFunctionality() {
			return this.isChangeSampleThroughExtEnabled && this.contextNodeAction?.is_change_sample_enabled;
		},
	},
	data() {
		return { changeContextParamsManually: false };
	},
};
</script>
<style lang="scss" scoped>
$side-padding: 20px;
.editor {
	padding: 0px $side-padding;
}
</style>
